/**
 * A two-pass job's clear image, beside the case and phone photos it was made from, waiting for
 * a person: "Looks right" runs pass 2 on it; "Regenerate" pays for a new pass 1. The cloud
 * starts nothing until one of them is pressed.
 */
import { useCallback, useEffect, useState } from "react";
import type { GenerationJobView } from "@genz/platform";
import { useServices } from "../app/context.js";
import { Modal } from "../ui/Modal.js";
import { errorDetail } from "./errorCode.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { useExpiredLinkRefresh } from "./generationIndex.js";

export interface ClearReviewProps {
  job: GenerationJobView;
  label: string;
  admin: boolean;
  onClose: () => void;
  onDecided: (job: GenerationJobView) => void;
  /** A decision was refused: the row may have moved on, so the list asks for it again. */
  onRefused: () => void;
}

interface ReferenceProps {
  url?: string | null;
  caption: string;
  testId: string;
  onError: () => void;
  onLoad: () => void;
}

function Reference({ url, caption, testId, onError, onLoad }: ReferenceProps) {
  if (!url) return null;
  return (
    <figure className="generate-review__figure">
      <img className="generate-review__img" src={url} alt={caption} data-testid={testId}
        onError={onError} onLoad={onLoad} />
      <figcaption className="caption">{caption}</figcaption>
    </figure>
  );
}

export function ClearReview({ job: listed, label, onClose, onDecided, onRefused }: ClearReviewProps) {
  const { client } = useServices();
  /** The job as the cloud has it now: a listed row may be old, with links signed for minutes. */
  const [job, setJob] = useState(listed);
  const reload = useCallback(async (id: string) => setJob(await client.getGenerationJob(id)), [client]);
  useEffect(() => { reload(listed.id).catch(() => undefined); }, [reload, listed.id]);
  const expired = useExpiredLinkRefresh(reload);
  const linkEvents = { onError: () => expired.onError(job.id), onLoad: () => expired.onLoad(job.id) };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: "approve" | "regenerate") => {
    setBusy(true);
    setError(null);
    try {
      onDecided(await client.reviewClearImage(job.id, decision, job.clearRef ?? ""));
    } catch (e) {
      setError(errorDetail(e));
      onRefused();
      // The next decision must name the image now on screen, not the one refused.
      await reload(job.id).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      testid="clear-review-modal"
      title={`Check the clear image: ${label}`}
      wide
      onClose={onClose}
      footer={
        <>
          <span className="caption">Regenerate makes a new clear image and uses credits.</span>
          <button type="button" className="btn" onClick={() => void decide("regenerate")} disabled={busy}
            data-testid="clear-review-regenerate">
            Regenerate
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void decide("approve")} disabled={busy}
            data-testid="clear-review-approve">
            Looks right
          </button>
        </>
      }
    >
      {error && (
        <p className="banner banner--danger" role="alert" data-testid="clear-review-error">
          <ErrorNotice detail={error} reference={job.id} />
        </p>
      )}
      <div className="generate-review">
        <Reference url={job.clearUrl} caption="Clear image" testId="clear-review-clear" {...linkEvents} />
        <div className="generate-review__refs">
          <Reference url={job.inputUrl} caption="Case reference" testId="clear-review-case" {...linkEvents} />
          <Reference url={job.productUrl} caption="Product reference" testId="clear-review-product" {...linkEvents} />
        </div>
      </div>
    </Modal>
  );
}
